/**
 * MergeStatementsWorkflow.jsx  —  VATICA V5
 *
 * Standalone "Merge Statements" tab — combines 2+ bank statements (any
 * bank, current or loan) into one consolidated working paper. Two ways
 * to supply the statements:
 *
 *   1. Upload Excels  — bring your own files (the .xlsx VATICA already
 *      generated for you). Doesn't depend on the system still having the
 *      record — useful if a statement was extracted on another device,
 *      or the original DB record/file was removed.
 *   2. Recent History — pick from the 5 most recently extracted
 *      statements on this system (optionally scoped to one client).
 *
 * Props:
 *   clientId    {number|null}  pre-selected client (when launched from ClientProfile)
 *   clientName  {string}
 */

import { useState, useRef, useEffect } from "react";
import {
  getMyClients,
  getMyBankStatements,
  mergeBankStatements,
  mergeBankStatementsFromExcel,
} from "./api";

const BANK_ICONS = {
  standard_bank:        "🏦",
  standard_bank_loan:   "📋",
  fnb:                   "🏛",
  eswatini_bank:         "🏢",
  nedbank_current:       "🏦",
  nedbank:               "📋",
  sbs_loan:              "🏠",
};

const RECENT_HISTORY_LIMIT = 5;

export default function MergeStatementsWorkflow({
  clientId: propClientId = null,
  clientName: propClientName = "",
}) {
  const [mode, setMode] = useState("upload"); // "upload" | "history"

  // ── Client scope (optional — narrows "recent history" to one client) ──────
  const [clientList, setClientList] = useState([]);
  const [selectedClient, setSelectedClient] = useState(
    propClientId ? { id: propClientId, name: propClientName } : null
  );

  useEffect(() => {
    getMyClients().then(d => setClientList(d || [])).catch(() => {});
  }, []);

  // ── Mode A: manual upload ──────────────────────────────────────────────────
  const [uploadFiles, setUploadFiles] = useState([]); // File[]
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  function addFiles(fileList) {
    const incoming = Array.from(fileList || []).filter(f =>
      f.name.toLowerCase().endsWith(".xlsx") || f.name.toLowerCase().endsWith(".xlsm")
    );
    if (!incoming.length) return;
    setUploadFiles(prev => {
      // De-dupe by name+size — re-dropping the same file twice shouldn't double it up.
      const existingKeys = new Set(prev.map(f => `${f.name}|${f.size}`));
      const merged = [...prev];
      for (const f of incoming) {
        const key = `${f.name}|${f.size}`;
        if (!existingKeys.has(key)) { merged.push(f); existingKeys.add(key); }
      }
      return merged;
    });
  }

  function removeUploadFile(idx) {
    setUploadFiles(prev => prev.filter((_, i) => i !== idx));
  }

  function handleUploadDrop(e) {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }

  // ── Mode B: recent history ─────────────────────────────────────────────────
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedForMerge, setSelectedForMerge] = useState(new Set());

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const all = await getMyBankStatements(selectedClient?.id || null);
      const recent = (all || []).slice(0, RECENT_HISTORY_LIMIT); // already ordered newest-first by the API
      setHistoryList(recent);
      setSelectedForMerge(prev => {
        const stillValid = new Set(recent.map(s => s.id));
        const next = new Set();
        prev.forEach(id => { if (stillValid.has(id)) next.add(id); });
        return next;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    if (mode === "history") loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedClient?.id]);

  function toggleMergeSelect(id) {
    setError("");
    setSelectedForMerge(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // ── Shared: tax year, merge action, result ─────────────────────────────────
  const [taxYear, setTaxYear] = useState("");
  const [merging, setMerging] = useState(false);
  const [error, setError]     = useState("");
  const [result, setResult]   = useState(null);

  const canMerge = mode === "upload"
    ? uploadFiles.length >= 2
    : selectedForMerge.size >= 2;

  async function handleMerge() {
    if (!canMerge || merging) return;
    setMerging(true);
    setError("");
    setResult(null);
    try {
      const meta = mode === "upload"
        ? await mergeBankStatementsFromExcel(
            uploadFiles,
            `VATICA_Merged_${Date.now()}.xlsx`,
            taxYear ? Number(taxYear) : null,
            selectedClient?.id || null,
          )
        : await mergeBankStatements(
            Array.from(selectedForMerge),
            `VATICA_Merged_${Date.now()}.xlsx`,
            taxYear ? Number(taxYear) : null,
          );
      setResult(meta);
      if (mode === "upload") setUploadFiles([]);
      else setSelectedForMerge(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setMerging(false);
    }
  }

  function switchMode(next) {
    setMode(next);
    setError("");
    setResult(null);
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24, maxWidth: 760, margin: "0 auto" }}>

      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>🔗 Merge Statements</div>
        <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 4 }}>
          Combine 2+ bank statements — any bank, current or loan — into one consolidated working paper
        </div>
      </div>

      {/* Mode toggle */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 4, background: "var(--surface)",
                      borderRadius: 12, padding: 4, border: "1px solid var(--border)" }}>
          {[
            { id: "upload",  icon: "📤", label: "Upload Excels" },
            { id: "history", icon: "🕘", label: "Recent History" },
          ].map(t => (
            <button key={t.id}
              onClick={() => switchMode(t.id)}
              style={{
                padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer",
                fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 600,
                background: mode === t.id
                  ? "linear-gradient(135deg,var(--ai-deep),var(--ai-blue))"
                  : "transparent",
                color: mode === t.id ? "#fff" : "var(--muted-light)",
                transition: "all 0.18s",
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: "var(--panel)", border: "1px solid var(--border)",
                    borderRadius: 16, overflow: "hidden" }}>

        {/* Client selector — optional in both modes; in History mode it scopes
            which 5 statements are shown, in Upload mode it just labels the
            output folder. */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600,
                           color: "var(--ai-blue)", marginBottom: 6 }}>
            Client {mode === "history" ? "(narrows recent history)" : "(optional)"}
          </label>
          <select
            value={selectedClient?.id || ""}
            onChange={e => {
              const c = clientList.find(x => String(x.id) === e.target.value);
              setSelectedClient(c ? { id: c.id, name: c.name } : null);
            }}
            style={{ width: "100%", background: "var(--surface)", color: "var(--text)",
                     border: "1px solid var(--border)", borderRadius: 8,
                     padding: "8px 10px", fontSize: 13, fontFamily: "'Outfit',sans-serif" }}>
            <option value="">— All clients —</option>
            {clientList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div style={{ padding: 20 }}>
          {mode === "upload" ? (
            <>
              {/* Drop zone */}
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleUploadDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? "var(--ai-blue)" : "var(--border)"}`,
                  borderRadius: 12, padding: "28px 16px", textAlign: "center",
                  cursor: "pointer", background: dragOver ? "rgba(59,158,255,0.06)" : "var(--surface)",
                  transition: "all 0.15s",
                }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>📁</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  Drop Excel files here, or click to browse
                </div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  The original .xlsx files VATICA generated for each statement (.xlsx / .xlsm)
                </div>
                <input ref={fileInputRef} type="file" multiple accept=".xlsx,.xlsm"
                  style={{ display: "none" }}
                  onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
              </div>

              {/* Selected files list */}
              {uploadFiles.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
                  {uploadFiles.map((f, idx) => (
                    <div key={`${f.name}|${f.size}`}
                      style={{ display: "flex", alignItems: "center", gap: 10,
                               background: "var(--surface)", border: "1px solid var(--border)",
                               borderRadius: 10, padding: "10px 14px" }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>📊</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 12.5,
                                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {f.name}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>
                          {(f.size / 1024).toFixed(0)} KB
                        </div>
                      </div>
                      <button onClick={() => removeUploadFile(idx)}
                        style={{ background: "rgba(239,68,68,0.08)", color: "var(--error)",
                                 border: "1px solid rgba(239,68,68,0.2)", borderRadius: 9,
                                 padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                 fontFamily: "'Outfit',sans-serif", flexShrink: 0 }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {uploadFiles.length === 1 && (
                <div style={{ marginTop: 8, fontSize: 11, color: "var(--muted)" }}>
                  Add at least one more file — merging needs 2 or more statements.
                </div>
              )}
            </>
          ) : (
            <>
              {/* Recent history picker */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                            marginBottom: 14 }}>
                <div style={{ fontSize: 11.5, color: "var(--muted)" }}>
                  Showing the {RECENT_HISTORY_LIMIT} most recent statements
                  {selectedClient ? ` for ${selectedClient.name}` : ""}
                </div>
                <button onClick={() => loadHistory()}
                  style={{ background: "transparent", border: "none", color: "var(--ai-blue)",
                           cursor: "pointer", fontSize: 12, fontFamily: "'Outfit',sans-serif" }}>
                  ↻ Refresh
                </button>
              </div>

              {historyLoading ? (
                <div style={{ textAlign: "center", color: "var(--muted)", fontSize: 13, padding: "20px 0" }}>
                  Loading...
                </div>
              ) : historyList.length < 2 ? (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <div style={{ fontSize: 32, opacity: 0.15, marginBottom: 8 }}>🕘</div>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>
                    {historyList.length === 0
                      ? "No recent statements found. Extract at least 2 to merge them, or switch to Upload Excels."
                      : "Only 1 recent statement found — extract or select at least one more to merge."}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {historyList.map(stmt => {
                    const checked = selectedForMerge.has(stmt.id);
                    return (
                      <label key={stmt.id}
                        style={{
                          display: "flex", gap: 12, alignItems: "center", cursor: "pointer",
                          background: checked ? "rgba(168,85,247,0.07)" : "var(--surface)",
                          border: `1px solid ${checked ? "rgba(168,85,247,0.3)" : "var(--border)"}`,
                          borderRadius: 10, padding: "10px 14px", transition: "border-color 0.18s",
                        }}>
                        <input type="checkbox" checked={checked}
                          onChange={() => toggleMergeSelect(stmt.id)}
                          style={{ width: 16, height: 16, flexShrink: 0, accentColor: "#a855f7" }} />
                        <span style={{ fontSize: 16, flexShrink: 0 }}>{BANK_ICONS[stmt.bank] || "🏦"}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 12.5 }}>
                            {stmt.bank_display || stmt.bank}
                            <span style={{ marginLeft: 8, fontSize: 10, color: "var(--muted)",
                                            background: "rgba(148,163,184,0.12)",
                                            padding: "1px 7px", borderRadius: 20, textTransform: "capitalize" }}>
                              {stmt.account_type}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: "var(--muted)" }}>
                            {stmt.transaction_count} transactions
                            {stmt.period ? ` · ${stmt.period}` : ""}
                            {stmt.filename_hint ? ` · ${stmt.filename_hint}` : ""}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {selectedForMerge.size === 1 && (
                <div style={{ marginTop: 8, fontSize: 11, color: "var(--muted)" }}>
                  Select at least one more statement — merging needs 2 or more.
                </div>
              )}
            </>
          )}

          {/* Tax year + action row */}
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginTop: 20 }}>
            <div style={{ flex: "0 0 180px" }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600,
                               color: "var(--muted)", marginBottom: 6 }}>
                Tax Year (optional)
              </label>
              <input type="number" placeholder="e.g. 2024"
                value={taxYear}
                onChange={e => setTaxYear(e.target.value)}
                style={{ width: "100%", background: "var(--surface)", color: "var(--text)",
                         border: "1px solid var(--border)", borderRadius: 8,
                         padding: "8px 10px", fontSize: 13, fontFamily: "'Outfit',sans-serif" }} />
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>
                2024 → Apr 2024 – Mar 2025. Leave blank to auto-detect.
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 200 }}>
              <button className="btn btn-purple"
                disabled={!canMerge || merging}
                onClick={handleMerge}
                style={{ opacity: canMerge ? 1 : 0.5 }}>
                {merging
                  ? "⏳ Merging..."
                  : `🔗 Merge${
                      mode === "upload"
                        ? (uploadFiles.length > 0 ? ` (${uploadFiles.length})` : "")
                        : (selectedForMerge.size > 0 ? ` (${selectedForMerge.size})` : "")
                    } & Download`}
              </button>
            </div>

            {mode === "upload" && uploadFiles.length > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ width: "auto" }}
                onClick={() => setUploadFiles([])}>
                Clear files
              </button>
            )}
            {mode === "history" && selectedForMerge.size > 0 && (
              <button className="btn btn-ghost btn-sm" style={{ width: "auto" }}
                onClick={() => setSelectedForMerge(new Set())}>
                Clear selection
              </button>
            )}
          </div>

          {error && (
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--error)" }}>
              ⚠ {error}
            </div>
          )}

          {result && (
            <div style={{ marginTop: 12, background: "rgba(34,211,168,0.07)",
                          border: "1px solid rgba(34,211,168,0.2)", borderRadius: 10,
                          padding: "12px 14px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--success)", marginBottom: 6 }}>
                ✅ Merged Excel downloaded
              </div>
              <div style={{ fontSize: 11.5, color: "var(--text)", lineHeight: 1.6 }}>
                {result.nStatements} statements · {result.nTransactions} transactions
                {result.taxYearLabel ? ` · Tax year ${result.taxYearLabel}` : ""}
                {(result.nDuplicates > 0 || result.nTransfers > 0 || result.missingMonths?.length > 0) && (
                  <div style={{ marginTop: 6 }}>
                    {result.nDuplicates > 0 && (
                      <div style={{ color: "var(--warning)" }}>
                        ⚠ {result.nDuplicates} possible duplicate transaction{result.nDuplicates > 1 ? "s" : ""} flagged — check the Combined Ledger sheet.
                      </div>
                    )}
                    {result.nTransfers > 0 && (
                      <div style={{ color: "var(--ai-blue)" }}>
                        🔁 {result.nTransfers} inter-account transfer candidate{result.nTransfers > 1 ? "s" : ""} flagged for review.
                      </div>
                    )}
                    {result.missingMonths?.length > 0 && (
                      <div style={{ color: "var(--warning)" }}>
                        📅 Missing months: {result.missingMonths.join(", ")}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
